/**
 * rev 17b — the Manage-button destination for a Basic/no-membership row with NO open period (the
 * `poolUserUnlinkOnly` predicate, hooks.ts): PeriodModal has nothing to open (there is no `period_id`
 * to call any period endpoint with), so this is a dedicated, minimal confirm dialog for the ONE action
 * such a row can still take — removing them from their current Zoom account right now. Calls the SAME
 * `bulk-force` endpoint the bulk bar uses, with `emails: [email]` (the PINNED CONTRACT — N=1 of the same
 * call, no separate singular endpoint).
 *
 * Same shell as ForceActionDialog (confirm → submit → render the outcome from the SAME shared
 * OUTCOME_KEY/outcomeText, forceOutcome.ts) — the only structural difference is the response shape:
 * `poolBulkForce` returns a flat `{email: code}` map + top-level `dry_run`, not the `{actions: counter}`
 * shape force-license-remove/force-unlink use, so the result panel below reads `result.results[email]`
 * rather than iterating an `actions` object.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "@/api/client";
import { Button, Modal, StatusPill } from "@/components/ui";
import { convergeUsersAfterBulkForce, poolKeys, useBulkForce } from "@/features/pool/hooks";
import { isOkCode, isPendingSweepCode, outcomeText } from "@/features/pool/forceOutcome";
import { bulkForceAbortMessage, isPartialCrashError } from "@/features/pool/bulkForceAbort";
import { PoolWriteModeNotice, usePoolWriteIsLive } from "@/features/pool/PoolWriteModeNotice";
import type { PoolBulkForceResult, PoolUser } from "@/api/types";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

export interface UnlinkOnlyDialogProps {
  user: PoolUser;
  onClose: () => void;
}

export function UnlinkOnlyDialog({ user, onClose }: UnlinkOnlyDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mut = useBulkForce();
  const isLive = usePoolWriteIsLive();

  const [result, setResult] = useState<PoolBulkForceResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partialFailure, setPartialFailure] = useState(false);

  function submit() {
    setError(null);
    setPartialFailure(false);
    mut.mutate(
      { action: "unlink", emails: [user.email] },
      {
        onSuccess: (res) => {
          setResult(res);
          void qc.invalidateQueries({ queryKey: poolKeys.history(user.email) });
          void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
          void qc.invalidateQueries({ queryKey: poolKeys.intents });
          if (!convergeUsersAfterBulkForce(qc, res.results, res.dry_run, "unlink")) {
            void qc.invalidateQueries({ queryKey: ["pool", "users"] });
          }
        },
        onError: (e) => {
          const abort = bulkForceAbortMessage(e, t);
          if (abort) {
            setError(abort);
          } else if (isPartialCrashError(e)) {
            setPartialFailure(true);
          } else {
            setError(errMessage(e, t("state.errorTitle")));
          }
        },
      },
    );
  }

  const code = result ? result.results[user.email] : undefined;

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.users.unlinkOnly.dialogTitle", { email: user.email })}
      size="sm"
      footer={
        result ? (
          <Button variant="secondary" onClick={onClose}>
            {t("action.close")}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("action.cancel")}
            </Button>
            {/* R1 late ruling: danger tone ONLY once the system's write mode is CONFIRMED live — see
                BulkForceDialog's matching comment. `isLive === false` and `null` (loading/errored)
                both render the neutral tone; only a confirmed `true` earns danger. */}
            <Button
              variant={isLive === true ? "danger" : "secondary"}
              loading={mut.isPending}
              onClick={submit}
            >
              {t("pool.users.unlinkOnly.confirmButton")}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {!result && (
          <>
            <PoolWriteModeNotice />
            {/* F1 body honesty (R1 late ruling): the live-danger sentence only renders once isLive is
                CONFIRMED true — off/dry-run/unknown all get the shared, honest test-mode body instead,
                never the "happens immediately" framing (mirrors BulkForceDialog). */}
            <p className="text-(length:--text-sm) text-(--text)">
              {isLive === true
                ? t("pool.users.unlinkOnly.dialogBody", { email: user.email })
                : t("pool.period.force.testModeBody")}
            </p>
            {/* F2 (rev17b addendum) — a standalone account's write support is fail-closed off by
                default; warn BEFORE the click so a guaranteed skip doesn't read as "the button did
                nothing" (same "announce, don't block" precedent as mirror_ready below). */}
            {user.standalone && (
              <p className="text-(length:--text-2xs) font-medium text-(--info)" role="note">
                {t("pool.users.standaloneWarning")}
              </p>
            )}
            {user.mirror_ready === false && (
              <p className="text-(length:--text-2xs) font-medium text-(--info)" role="note">
                {t("pool.period.force.mirrorPendingNote")}
              </p>
            )}
          </>
        )}

        {/* The server's refusal, rendered verbatim (409/503/422 — F3's shared renderer), never
            paraphrased, exactly like every other refusal elsewhere in this module. */}
        {error && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}

        {/* F4 — a failed POST outside the recognized 409/503/422 shapes: the per-email commit is a
            server guarantee, so this email may have already gone through even though the request
            itself failed client-side. */}
        {partialFailure && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {t("pool.users.bulk.partialFailureNote")}
            <Link to="/pool/intents" className="underline underline-offset-2">
              {t("pool.tabs.intents")}
            </Link>
          </p>
        )}

        {result && code && (
          <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                {t("pool.period.force.resultTitle")}
              </p>
              {/* R1 late ruling — accepted_pending_sweep gets its own "info" tone: the write DID land
                  (liveBadge is accurate copy), but the read-back couldn't confirm it in time, so it's
                  neither the verified "ok" tone nor the "nothing was sent" noWriteBadge/neutral pair. */}
              <StatusPill
                tone={
                  result.dry_run
                    ? "warn"
                    : isOkCode(code)
                      ? "ok"
                      : isPendingSweepCode(code)
                        ? "info"
                        : "neutral"
                }
              >
                {result.dry_run
                  ? t("pool.period.force.dryRunBadge")
                  : isOkCode(code) || isPendingSweepCode(code)
                    ? t("pool.period.force.liveBadge")
                    : t("pool.period.force.noWriteBadge")}
              </StatusPill>
            </div>
            <p className="text-(length:--text-sm) text-(--text)">{outcomeText(code, t)}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
